import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import allegatiQuery from '../queries/allegatiQuery';

/**
 * Hook that provides dynamic filtering for Allegati
 * Builds the where clause dynamically based on active filters
 */
export const useDynamicFilter = (
  setLoading,
  setItems,
  setHasNextPage,
  setTotalCount,
  setLoadingMore
) => {
  const [activeFilters, setActiveFilters] = useState([]);
  const filterTimeoutRef = useRef(null);
  
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  
  /**
   * Build a dynamic where clause based on active filters
   */
  const buildWhereClause = useCallback((params = {}) => {
    const filters = params.filters || activeFilters;
    
    // Start with an empty where clause
    const whereClause = {};
    
    // Add filter conditions
    if (filters && filters.length > 0) {
      // Group filters by logical operator
      const filtersByOperator = filters.reduce((acc, filter) => {
        const op = filter.logicalOperator || "AND";
        if (!acc[op]) acc[op] = [];
        
        const condition = {};
        const operator = filter.operator || 'equalTo';
        
        // Check if the value is already a complex filter object (like for pointer fields)
        if (typeof filter.value === 'object' && filter.value !== null && !Array.isArray(filter.value)) {
          condition[filter.column] = filter.value;
        } else if (operator === 'exists') {
          condition[filter.column] = { exists: filter.value };
        } else if (typeof filter.value === 'boolean') {
          condition[filter.column] = { equalTo: filter.value };
        } else {
          // Use the selected operator from the filter
          let operatorConfig;
          
          switch (operator) {
            case 'notEqualTo':
              operatorConfig = { notEqualTo: filter.value };
              break;
            case 'greaterThan':
              operatorConfig = { greaterThan: filter.value };
              break;
            case 'lessThan':
              operatorConfig = { lessThan: filter.value };
              break;
            case 'greaterThanOrEqualTo':
              operatorConfig = { greaterThanOrEqualTo: filter.value };
              break;
            case 'lessThanOrEqualTo':
              operatorConfig = { lessThanOrEqualTo: filter.value };
              break;
            case 'contains':
              operatorConfig = { includes: filter.value };
              break;
            case 'equalTo':
            default:
              operatorConfig = { equalTo: filter.value };
              break;
          }
          
          condition[filter.column] = operatorConfig;
        }
        acc[op].push(condition);
        
        return acc;
      }, {});
      
      // Create the overall structure for the where clause
      const conditions = [];
      
      // Process AND conditions
      if (filtersByOperator.AND && filtersByOperator.AND.length > 0) {
        if (filtersByOperator.AND.length === 1 && conditions.length === 0) {
          // If there's only one AND condition, apply it directly
          const condition = filtersByOperator.AND[0];
          const key = Object.keys(condition)[0];
          whereClause[key] = condition[key];
        } else {
          // Multiple AND conditions
          conditions.push(...filtersByOperator.AND);
        }
      }
      
      // Process OR conditions
      if (filtersByOperator.OR && filtersByOperator.OR.length > 0) {
        if (filtersByOperator.OR.length === 1 && conditions.length === 0) {
          // If there's only one OR condition, apply it directly
          const condition = filtersByOperator.OR[0];
          const key = Object.keys(condition)[0];
          whereClause[key] = condition[key];
        } else if (filtersByOperator.OR.length > 1) {
          // Multiple OR conditions
          conditions.push({ OR: filtersByOperator.OR });
        } else {
          // Single OR condition combined with other conditions
          conditions.push(filtersByOperator.OR[0]);
        }
      }
      
      // Combine all conditions with AND if we have multiple conditions
      if (conditions.length > 1) {
        whereClause.AND = conditions;
      } else if (conditions.length === 1 && !whereClause[Object.keys(conditions[0])[0]]) {
        // If we only have one condition and it's not already directly applied
        const condition = conditions[0];
        const key = Object.keys(condition)[0];
        
        // Check if it's an OR condition
        if (key === 'OR') {
          whereClause.OR = condition.OR;
        } else {
          whereClause[key] = condition[key];
        }
      }
    }
    
    return whereClause;
  }, [activeFilters]);

  /**
   * Fetch data with the dynamic where clause
   */
  const fetchFilteredData = useCallback(async (params = {}) => {
    const skip = params.skip || 0;
    const isRefresh = params.isRefresh || false;
    
    if (skip === 0 || isRefresh) {
      setLoading(true);
      if (isRefresh) {
        setItems([]);
      }
    } else {
      setLoadingMore(true);
    }

    try {
      const accessToken = await getTokenSilently();
      
      // Build the where clause
      const where = buildWhereClause(params);
      
      const variables = {
        first: 20,
        skip: skip,
        where: where,
        order: ["createdAt_DESC"],
      };

      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/graphql?raw=true`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: allegatiQuery,
            variables,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const result = data.allegatis;
        const newItems = result.edges.map(edge => edge.node) || [];
        
        if (skip === 0 || isRefresh) {
          setItems(newItems);
        } else {
          setItems(prev => [...prev, ...newItems]);
        }
        
        setTotalCount(result.count || 0);
        setHasNextPage(result.pageInfo?.hasNextPage || false);
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch allegati:", response.status, errorText);
      }
    } catch (error) {
      console.error("Error fetching allegati:", error);
    }

    setLoading(false);
    setLoadingMore(false);
  }, [getTokenSilently, isProd, buildWhereClause, setLoading, setItems, setHasNextPage, setTotalCount, setLoadingMore]);

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback((newFilters) => {
    setActiveFilters(newFilters);
    
    // Use a timeout to batch filter changes and prevent multiple queries
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    filterTimeoutRef.current = setTimeout(() => {
      fetchFilteredData({ filters: newFilters, skip: 0, isRefresh: true });
    }, 50);
  }, [fetchFilteredData]);

  /**
   * Clear all filters
   */
  const clearAll = useCallback(() => {
    setActiveFilters([]);
    fetchFilteredData({ filters: [], skip: 0, isRefresh: true });
  }, [fetchFilteredData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, []);

  return {
    activeFilters,
    fetchFilteredData,
    handleFilterChange,
    clearAll
  };
};

export default useDynamicFilter;